/**
 * IPAMDashboard — /networks (resumen del IPAM).
 *
 * Panel de un vistazo: cuánto espacio hay, cómo se está usando y qué necesita
 * atención. Sigue la estética del estándar de listados:
 *  · misma cabecera (`.list-head`) y mismos chips de KPI que las listas, cada uno
 *    enlazando a su pantalla;
 *  · iconos de `@heroicons/react` (los mismos que el menú lateral de Redes), ni
 *    un emoji suelto;
 *  · colores solo con las variables del sistema de diseño.
 *
 * No lleva botones de acceso rápido: para eso está el menú lateral, y repetirlos
 * aquí solo ocupaba sitio. Los números sí son clicables (llevan a su listado).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GlobeAltIcon,
  ServerStackIcon,
  CircleStackIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  WifiIcon,
  LinkIcon,
  RectangleStackIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import api from '../services/api';
import { subnetTypeLabel } from '../utils/ipamTypes';
import { ciTypeLabel } from '../utils/ipamCi';

/** Color de una barra de ocupación, con los tonos del sistema de diseño. */
function tone(pct) {
  if (pct >= 90) return 'var(--red-600)';
  if (pct >= 70) return 'var(--amber-600)';
  return 'var(--green-600)';
}

/** Barra de progreso del estándar (la misma que usan las columnas de las listas). */
function Bar({ pct, width = 90 }) {
  const value = Math.max(0, Math.min(100, pct || 0));
  return (
    <span style={{
      display: 'inline-block', width, height: 7, borderRadius: 4,
      background: 'var(--bg-toolbar)', overflow: 'hidden', flexShrink: 0,
    }}>
      <span style={{ display: 'block', width: `${value}%`, height: '100%', background: tone(value) }} />
    </span>
  );
}

export default function IPAMDashboard({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [blocks, setBlocks] = useState([]);
  const [subnets, setSubnets] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const [ipCount, setIpCount] = useState(0);
  const [vlans, setVlans] = useState([]);
  const [vips, setVips] = useState([]);
  const [dhcpStats, setDhcpStats] = useState(null);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [b, s, a, i, v, vg, dhcp] = await Promise.all([
        api.getIpamBlocks(companyId),
        api.getIpamSubnets(companyId),
        api.getIpamAssignments(companyId),
        api.getIpamIPs(companyId, { limit: 1000 }),
        api.getIpamVlans(companyId).catch(() => ({ data: [] })),
        api.getIpamVirtualGroups(companyId).catch(() => ({ data: [] })),
        api.getDhcpStats(companyId).catch(() => ({ data: null })),
      ]);
      setBlocks(b.data || []);
      setSubnets(s.data || []);
      const allAssignments = a.data || [];
      setTotalAssignments(allAssignments.length);
      setAssignments(allAssignments.slice(0, 8));
      setIpCount((i.data || []).length);
      setVlans(v.data || []);
      setVips(vg.data || []);
      setDhcpStats(dhcp.data || null);
    } catch (e) {
      setError(e.message || 'Error cargando el resumen del IPAM');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ── Cuentas ────────────────────────────────────────────────────────────── */
  const k = useMemo(() => {
    const totalAddresses = blocks.reduce((sum, b) => sum + (b.block_addresses || 0), 0);
    const totalAssigned = blocks.reduce((sum, b) => sum + (b.occupied_addresses || 0), 0);
    const usedPct = totalAddresses > 0 ? Math.round((totalAssigned / totalAddresses) * 100) : null;

    const subnetsFull = subnets.filter(s => s.usable_ips > 0 && (s.occupation_pct ?? 0) >= 100).length;
    const subnetsFree = subnets.filter(s => !(s.used_ips > 0)).length;
    const subnetsDelegated = subnets.filter(s => s.viewer_is_owner === false || s.delegated_to_company_name).length;
    const subnetsAvg = subnets.length
      ? Math.round(subnets.reduce((sum, s) => sum + (s.occupation_pct || 0), 0) / subnets.length)
      : null;

    const vlansFree = vlans.filter(v => !(v.subnets || []).length).length;
    const vipsNoIp = vips.filter(v => !v.ip).length;
    const vipsNoMembers = vips.filter(v => !(v.members || []).length).length;

    return {
      totalAddresses, totalAssigned, usedPct, freePct: usedPct == null ? null : 100 - usedPct,
      subnetsFull, subnetsFree, subnetsDelegated, subnetsAvg,
      vlansFree, vipsNoIp, vipsNoMembers,
      topSubnets: [...subnets]
        .filter(s => s.usable_ips > 0)
        .sort((a, b) => (b.occupation_pct || 0) - (a.occupation_pct || 0))
        .slice(0, 5),
      topBlocks: [...blocks]
        .sort((a, b) => (b.occupation_pct || 0) - (a.occupation_pct || 0))
        .slice(0, 6),
    };
  }, [blocks, subnets, vlans, vips]);

  const kpiChips = [
    { key: 'blocks', icon: GlobeAltIcon, label: tr('ipam.blocks_title', 'Bloques IP'), value: blocks.length, path: '/networks/blocks' },
    { key: 'subnets', icon: RectangleStackIcon, label: tr('ipam.subnets_title', 'Subredes'), value: subnets.length, path: '/networks/subnets' },
    { key: 'ips', icon: CircleStackIcon, label: tr('ip.title', 'Direcciones IP'), value: ipCount, path: '/networks/ips' },
    { key: 'assignments', icon: LinkIcon, label: tr('dash.assignments', 'Asignaciones'), value: totalAssignments, path: '/networks/assignments' },
    { key: 'vlans', icon: TableCellsIcon, label: tr('vlan.title', 'VLANs'), value: vlans.length, path: '/networks/vlans' },
    { key: 'vips', icon: ShieldCheckIcon, label: tr('vip.title', 'VIPs / CARP'), value: vips.length, path: '/networks/vips' },
    { key: 'dhcp', icon: WifiIcon, label: tr('dhcp.title', 'Rangos DHCP'), value: dhcpStats?.dhcp_ranges ?? 0, path: '/networks/dhcp' },
  ];

  const cardStyle = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md, 10px)',
  };
  const cardHeader = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)',
  };

  return (
    <IPAMLayout>
      <div className="list-page">
        {error && (
          <div className="alert alert-danger py-2 d-flex justify-content-between">
            <span>{error}</span>
            <button className="btn-close" onClick={() => setError('')} />
          </div>
        )}

        {/* ══ Cabecera, igual que la de una lista ══ */}
        <div className="list-head">
          <div className="list-head-left">
            <GlobeAltIcon className="list-head-icon" />
            <div>
              <h1 className="list-title">{tr('dash.title', 'Redes / IPAM')}</h1>
              <p className="list-subtitle">
                {loading
                  ? tr('msg.loading', 'Cargando…')
                  : `${blocks.length} ${tr('ipam.block_plural', 'bloques')} · ${subnets.length} ${tr('ipam.subnet_plural', 'subredes')}`
                    + ` · ${k.totalAddresses.toLocaleString()} ${tr('dash.addresses', 'direcciones')}`}
              </p>
            </div>
          </div>
        </div>

        {/* ══ Uso global del espacio de direcciones ══ */}
        <div style={{ ...cardStyle, padding: '16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div className="list-hint">{tr('dash.global_use', 'Uso del espacio de direcciones')}</div>
              <div style={{ fontSize: '1.8em', fontWeight: 700, color: k.usedPct == null ? 'var(--text-primary)' : tone(k.usedPct) }}>
                {k.usedPct == null ? '—' : `${k.usedPct}%`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              <div>
                <div className="list-hint">{tr('dash.used', 'En uso')}</div>
                <div className="list-code" style={{ fontWeight: 700 }}>{k.totalAssigned.toLocaleString()}</div>
              </div>
              <div>
                <div className="list-hint">{tr('dash.free', 'Libres')}</div>
                <div className="list-code" style={{ fontWeight: 700, color: 'var(--green-600)' }}>
                  {Math.max(0, k.totalAddresses - k.totalAssigned).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="list-hint">{tr('dash.total', 'Total')}</div>
                <div className="list-code" style={{ fontWeight: 700 }}>{k.totalAddresses.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div style={{ height: 12, borderRadius: 6, background: 'var(--bg-toolbar)', overflow: 'hidden', marginTop: 12 }}>
            <div style={{
              width: `${k.usedPct ?? 0}%`, height: '100%',
              background: tone(k.usedPct ?? 0), transition: 'width .3s',
            }} />
          </div>
        </div>

        {/* ══ Chips de KPI: cada uno lleva a su pantalla ══ */}
        <div className="list-kpis list-kpis--chips" style={{ marginBottom: 16 }}>
          {kpiChips.map(chip => {
            const Icon = chip.icon;
            return (
              <button
                key={chip.key}
                type="button"
                className="list-kpi-chip"
                onClick={() => navigate(chip.path)}
                title={`${tr('dash.go_to', 'Ir a')} ${chip.label}`}
              >
                <span className="list-kpi-value">{chip.value}</span>
                <span className="list-kpi-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Icon style={{ width: 13, height: 13 }} />
                  {chip.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="row g-3">
          {/* ══ Subredes ══ */}
          <div className="col-lg-6">
            <div style={{ ...cardStyle, height: '100%' }}>
              <div style={cardHeader}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <RectangleStackIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  {tr('dash.subnets_panel', 'Subredes')}
                </strong>
                <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/networks/subnets')}>
                  {tr('dash.see_all', 'Ver todas')} →
                </button>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div>
                    <div className="list-hint">{tr('dash.subnets_total', 'Registradas')}</div>
                    <div className="list-code" style={{ fontWeight: 700 }}>{subnets.length}</div>
                  </div>
                  <div>
                    <div className="list-hint">{tr('dash.subnets_avg', 'Ocupación media')}</div>
                    <div className="list-code" style={{ fontWeight: 700 }}>
                      {k.subnetsAvg == null ? '—' : `${k.subnetsAvg}%`}
                    </div>
                  </div>
                  <div>
                    <div className="list-hint">{tr('dash.subnets_full', 'Agotadas')}</div>
                    <div className="list-badge list-badge--off" style={{ fontWeight: 700 }}>{k.subnetsFull}</div>
                  </div>
                  <div>
                    <div className="list-hint">{tr('dash.subnets_free', 'Sin usar')}</div>
                    <div className="list-badge list-badge--on" style={{ fontWeight: 700 }}>{k.subnetsFree}</div>
                  </div>
                  <div>
                    <div className="list-hint">{tr('dash.subnets_delegated', 'Delegadas')}</div>
                    <div className="list-badge list-badge--warn" style={{ fontWeight: 700 }}>{k.subnetsDelegated}</div>
                  </div>
                </div>

                <div className="list-hint" style={{ marginBottom: 6 }}>
                  {tr('dash.subnets_top', 'Las que más se llenan')}
                </div>
                {k.topSubnets.length === 0 && (
                  <span className="list-hint">{tr('dash.subnets_none', 'Todavía no hay subredes con IPs usables.')}</span>
                )}
                {k.topSubnets.map(s => (
                  <div
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/networks/subnets?destacar=${s.id}&abrir=1`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/networks/subnets?destacar=${s.id}&abrir=1`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: 'pointer' }}
                    title={`${tr('dash.go_to', 'Ir a')} ${s.cidr}`}
                  >
                    <span className="list-code" style={{ fontWeight: 600, minWidth: 110 }}>{s.cidr}</span>
                    <Bar pct={s.occupation_pct} />
                    <span className="list-hint" style={{ color: tone(s.occupation_pct || 0), minWidth: 36 }}>
                      {s.occupation_pct ?? 0}%
                    </span>
                    <span className="list-hint">{s.used_ips}/{s.usable_ips}</span>
                    <span className="list-badge" style={{ marginLeft: 'auto' }}>{subnetTypeLabel(s.type)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ══ DHCP ══ */}
          <div className="col-lg-6">
            <div style={{ ...cardStyle, height: '100%' }}>
              <div style={cardHeader}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <WifiIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  {tr('dash.dhcp_panel', 'DHCP')}
                </strong>
                <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/networks/dhcp')}>
                  {tr('dash.see_all', 'Ver todas')} →
                </button>
              </div>
              <div style={{ padding: '12px 16px' }}>
                {!dhcpStats && <span className="list-hint">{tr('dash.no_dhcp', 'Sin datos de DHCP.')}</span>}
                {dhcpStats && (
                  <>
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 12 }}>
                      <div>
                        <div className="list-hint">{tr('dash.dhcp_ranges', 'Rangos')}</div>
                        <div className="list-code" style={{ fontWeight: 700 }}>{dhcpStats.dhcp_ranges}</div>
                      </div>
                      <div>
                        <div className="list-hint">{tr('dash.dhcp_ips', 'IPs en rangos')}</div>
                        <div className="list-code" style={{ fontWeight: 700 }}>{(dhcpStats.total_range_ips || 0).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="list-hint">{tr('dash.dhcp_res', 'Reservas')}</div>
                        <div className="list-code" style={{ fontWeight: 700 }}>{dhcpStats.reservations}</div>
                      </div>
                      <div>
                        <div className="list-hint">{tr('dash.dhcp_subnets', 'Subredes con DHCP')}</div>
                        <div className="list-code" style={{ fontWeight: 700 }}>{dhcpStats.subnets_with_dhcp}</div>
                      </div>
                    </div>
                    <div className="list-hint" style={{ marginBottom: 6 }}>
                      {tr('dash.dhcp_use', 'Uso de los rangos')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Bar pct={dhcpStats.usage_pct} width={160} />
                      <span className="list-hint" style={{ color: tone(dhcpStats.usage_pct || 0), fontWeight: 700 }}>
                        {dhcpStats.usage_pct}%
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ══ Ocupación por bloque ══ */}
          <div className="col-lg-7">
            <div style={{ ...cardStyle, height: '100%' }}>
              <div style={cardHeader}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <GlobeAltIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  {tr('dash.blocks_panel', 'Ocupación por bloque')}
                </strong>
                <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/networks/blocks')}>
                  {tr('dash.see_all', 'Ver todas')} →
                </button>
              </div>
              <div style={{ padding: '12px 16px' }}>
                {loading && <span className="list-hint">{tr('msg.loading', 'Cargando…')}</span>}
                {!loading && blocks.length === 0 && <span className="list-hint">{tr('ipam.block_empty', 'No hay bloques IP')}</span>}
                {k.topBlocks.map(b => {
                  const pct = b.version === 6 ? 0 : (b.occupation_pct || 0);
                  return (
                    <div
                      key={b.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/networks/blocks?destacar=${b.id}&abrir=1`)}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/networks/blocks?destacar=${b.id}&abrir=1`); }}
                      style={{ padding: '5px 0', cursor: 'pointer' }}
                      title={`${tr('dash.go_to', 'Ir a')} ${b.cidr}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="list-code" style={{ fontWeight: 600, minWidth: 130 }}>{b.cidr}</span>
                        <Bar pct={pct} width={110} />
                        <span className="list-hint" style={{ color: b.version === 6 ? undefined : tone(pct) }}>
                          {b.version === 6 ? tr('dash.ipv6', 'IPv6') : `${pct}%`}
                        </span>
                        <span className="list-hint" style={{ marginLeft: 'auto' }}>
                          {b.subnets_count || 0} {tr('ipam.subnets', 'subredes').toLowerCase()}
                        </span>
                      </div>
                      {b.name && <div className="list-hint" style={{ paddingLeft: 2 }}>{b.name}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ══ VLANs y VIPs ══ */}
          <div className="col-lg-5">
            <div style={{ ...cardStyle, height: '100%' }}>
              <div style={cardHeader}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Squares2X2Icon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  {tr('dash.vlan_vip_panel', 'VLANs y VIPs')}
                </strong>
              </div>
              <div style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <TableCellsIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  <span>{tr('vlan.title', 'VLANs')}</span>
                  <button
                    type="button"
                    className="list-badge"
                    style={{ marginLeft: 'auto', cursor: 'pointer' }}
                    onClick={() => navigate('/networks/vlans')}
                  >
                    {vlans.length}
                  </button>
                  <span className="list-badge list-badge--on" title={tr('dash.vlans_free_hint', 'VLANs sin ninguna subred asignada')}>
                    {k.vlansFree} {tr('vlan.free', 'libre').toLowerCase()}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <ShieldCheckIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  <span>{tr('vip.title', 'VIPs / CARP')}</span>
                  <button
                    type="button"
                    className="list-badge"
                    style={{ marginLeft: 'auto', cursor: 'pointer' }}
                    onClick={() => navigate('/networks/vips')}
                  >
                    {vips.length}
                  </button>
                  {k.vipsNoIp > 0 && (
                    <span className="list-badge list-badge--warn" title={tr('dash.vips_no_ip_hint', 'Grupos sin IP virtual asignada')}>
                      {k.vipsNoIp} {tr('vip.no_ip', 'sin asignar').toLowerCase()}
                    </span>
                  )}
                  {k.vipsNoMembers > 0 && (
                    <span className="list-badge list-badge--off" title={tr('dash.vips_no_members_hint', 'Grupos sin ningún miembro')}>
                      {k.vipsNoMembers} {tr('vip.no_members', 'sin miembros').toLowerCase()}
                    </span>
                  )}
                </div>
                <div className="list-hint" style={{ marginTop: 10 }}>
                  {tr('dash.vips_hint', 'Un grupo VIP reúne los CIs que comparten una IP (HA). Una VLAN libre se puede asignar a una subred.')}
                </div>
              </div>
            </div>
          </div>

          {/* ══ Asignaciones recientes ══ */}
          <div className="col-12">
            <div style={cardStyle}>
              <div style={cardHeader}>
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <ServerStackIcon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} />
                  {tr('dash.recent_assignments', 'Asignaciones recientes')}
                </strong>
                <button className="btn btn-link btn-sm py-0 text-decoration-none" onClick={() => navigate('/networks/assignments')}>
                  {tr('dash.see_all', 'Ver todas')} →
                </button>
              </div>
              {assignments.length === 0 ? (
                <div style={{ padding: '16px' }}>
                  <span className="list-hint">{tr('dash.no_assignments', 'Todavía no hay IPs asignadas a ningún equipo.')}</span>
                </div>
              ) : (
                /* Filas con los tokens del panel, no `<table>`: dentro de una
                   tarjeta las tablas de Bootstrap salen con fondo oscuro. */
                <div style={{ padding: '12px 16px' }}>
                  {assignments.map(a => (
                    <div
                      key={a.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        padding: '6px 0', borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <span className="list-code" style={{ fontWeight: 700, minWidth: 110 }}>{a.ip}</span>
                      <span className="list-code">{a.subnet_cidr || '—'}</span>
                      <span>
                        {a.object_name || `#${a.object_id}`}
                        <span className="list-hint"> {ciTypeLabel(a.object_type)}</span>
                      </span>
                      <span className="list-badge">{(a.assignment_type || 'static').toUpperCase()}</span>
                      {a.interface_ref && <span className="list-code">{a.interface_ref}</span>}
                      {a.dns_name && <span className="list-code">{a.dns_name}</span>}
                      {a.assigned_by && <span className="list-hint" style={{ marginLeft: 'auto' }}>{a.assigned_by}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </IPAMLayout>
  );
}
